import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Phone } from "lucide-react";
import { CLINIC, DISCLAIMER } from "../api";
import { useLanguage } from "../i18n";

export default function Footer() {
    const { t } = useLanguage();
    return (
        <footer className="bg-navy text-white" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-20">
                <div className="grid gap-12 md:grid-cols-3">
                    <div>
                        <p className="font-display text-xl font-semibold">Dr. Krishnakanth AVB</p>
                        <p className="mt-1 font-mono-label text-[0.62rem] text-slate-400">Consultant Urologist</p>
                        <p className="mt-4 text-sm leading-relaxed text-slate-300">
                            MBBS, MS (General Surgery), MCh (Urology)<br />
                            {CLINIC.hospital}<br />
                            {CLINIC.address}
                        </p>
                        <div className="mt-5 flex flex-col gap-2 text-sm">
                            <a href={CLINIC.phoneHref} data-testid="footer-call-link"
                                className="inline-flex items-center gap-2 text-slate-300 transition-colors hover:text-white">
                                <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {CLINIC.phoneDisplay}
                            </a>
                            <a href={CLINIC.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
                                className="inline-flex items-center gap-2 text-slate-300 transition-colors hover:text-white">
                                <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" /> WhatsApp
                            </a>
                            <a href={`mailto:${CLINIC.email}`} data-testid="footer-email-link"
                                className="inline-flex items-center gap-2 text-slate-300 transition-colors hover:text-white">
                                {CLINIC.email}
                            </a>
                        </div>
                        <div className="mt-6">
                            <p className="font-mono-label text-[0.62rem] text-slate-400">{t("follow")}</p>
                            <div className="mt-3 flex gap-3">
                                <a href={CLINIC.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram"
                                    aria-label="Instagram — @uro_bytes"
                                    className="rounded-full border border-white/15 p-2.5 text-slate-300 transition-all duration-300 hover:border-accent hover:text-white">
                                    <Instagram className="h-4 w-4" />
                                </a>
                            </div>
                            <p className="mt-2 text-xs text-slate-500">@uro_bytes on Instagram.</p>
                        </div>
                    </div>
                    <nav aria-label="Footer">
                        <p className="font-mono-label text-[0.62rem] text-slate-400">Explore</p>
                        <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                            {[
                                ["About", "/about"], ["Conditions", "/conditions"], ["Treatments", "/treatments"],
                                ["Patient Education", "/patient-education"], ["Research", "/research"],
                                ["Contact", "/contact"], ["Book an Appointment", "/appointment"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link to={to} data-testid={`footer-${to.slice(1) || "home"}-link`}
                                        className="text-slate-300 transition-colors duration-200 hover:text-white">
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div>
                        <p className="font-mono-label text-[0.62rem] text-slate-400">Legal</p>
                        <ul className="mt-4 space-y-2.5 text-sm">
                            <li><Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-slate-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/terms-of-use" data-testid="footer-terms-link" className="text-slate-300 hover:text-white transition-colors">Terms of Use</Link></li>
                            <li><Link to="/medical-disclaimer" data-testid="footer-disclaimer-link" className="text-slate-300 hover:text-white transition-colors">Medical Disclaimer</Link></li>
                        </ul>
                        <p className="mt-6 border-t border-white/10 pt-5 text-xs leading-relaxed text-slate-400">{DISCLAIMER}</p>
                    </div>
                </div>
                <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center">
                    <p>© 2026 Dr. Krishnakanth AVB. All rights reserved.</p>
                    <p className="font-mono-label text-[0.58rem]">Madurai · Tamil Nadu · India</p>
                </div>
            </div>
        </footer>
    );
}
